/**
 * Company User Portal – Login for users created by company admin (Manager, Store Manager, Sales, Cashier, etc.)
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import styles from './AdminLogin.module.css';
import './AdminLoginShell.css';
import { RBACService } from '../utils/rbac';
import { loginWithEmail, persistAuthSession, parseLoginError } from '../services/authService';

const nacLogoUrl = `${process.env.PUBLIC_URL || ''}/nac_logo_tight_circle.png`;
const retailImgUrl = `${process.env.PUBLIC_URL || ''}/landing/login_photo_staff.jpg`;

interface LoginForm {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noAutofillFocus, setNoAutofillFocus] = useState({ email: false, password: false });

  useEffect(() => {
    document.documentElement.classList.add('user-login-active');
    return () => document.documentElement.classList.remove('user-login-active');
  }, []);

  useEffect(() => {
    const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
    if (!isDebugMode) {
      const token = localStorage.getItem('authToken');
      if (token === 'debug-token') {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userRoleId');
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        RBACService.clearUser();
      }
    }
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      const { token, user, portalMismatch } = await loginWithEmail(
        formData.email,
        formData.password,
        'user',
      );

      persistAuthSession(user, token);
      setIsLoading(false);

      if (portalMismatch) {
        setErrors({ general: `${portalMismatch.message} Redirecting...` });
        setTimeout(() => navigate(portalMismatch.redirectTo), 2000);
        return;
      }

      navigate('/dashboard');
    } catch (error: unknown) {
      setIsLoading(false);
      setErrors({ general: parseLoginError(error, formData.email) });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement;
    const field = (target.dataset.field || target.name) as keyof LoginForm;
    const { value } = target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors] || errors.general) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FormErrors];
        delete next.general;
        return next;
      });
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <Link to="/" className={styles.brand}>
          <img src={nacLogoUrl} alt="NAC" className={styles.brandLogo} />
          <span>NAC Easy Solution</span>
        </Link>
        <span className={styles.portalTag}>Staff portal</span>
      </header>

      <main className={styles.main}>
        <section className={styles.formPanel} aria-label="Welcome back">
          <p className={styles.badge}>
            <span className={styles.badgeDot} aria-hidden />
            Staff portal
          </p>
          <h1 className={styles.title}>Welcome back</h1>
          <p className={styles.subtitle}>
            Sign in to open your till, make sales, and access your company portal.
          </p>

          <form
            noValidate
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            {errors.general && (
              <div className={styles.errorBanner}>
                <AlertCircle size={15} />
                <span>{errors.general}</span>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="email">Email address</label>
              <input
                id="email"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                type="text"
                name="login_email"
                data-field="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setNoAutofillFocus((p) => ({ ...p, email: true }))}
                placeholder="you@company.com"
                disabled={isLoading}
                readOnly={!noAutofillFocus.email}
                inputMode="email"
                autoComplete="username"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="password">Password</label>
              <div className={styles.passWrap}>
                <input
                  id="password"
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  type={showPassword ? 'text' : 'password'}
                  name="login_password"
                  data-field="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setNoAutofillFocus((p) => ({ ...p, password: true }))}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  readOnly={!noAutofillFocus.password}
                  autoComplete="current-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                />
                <button
                  type="button"
                  className={styles.showBtn}
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
            </div>

            <div className={styles.actions}>
              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className={styles.spinner} />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <p className={styles.switchNote}>
            Company administrator?{' '}
            <button
              type="button"
              className={styles.switchLink}
              onClick={() => navigate('/admin/login')}
              disabled={isLoading}
            >
              Admin login
            </button>
          </p>
        </section>

        <aside className={styles.visual} aria-hidden>
          <img src={retailImgUrl} alt="" className={styles.visualImg} />
        </aside>
      </main>

      <footer className={styles.footer}>
        ©{new Date().getFullYear()} - NAC Software and Tech Solutions. Made in Bhutan.
      </footer>
    </div>
  );
};

export default Login;
